const fitAttribute = 'data-presentation-display-fit';
const fitProperty = '--presentation-display-font-size';
const selector = '.wp-block-heading.stgb-presentation-behind, .wp-block-heading.is-style-stgb-presentation-display';

// WordPress can turn an authored 8.4rem into a clamp with a large minimum.
// Its maximum is the design size; the Presentation supplies the fluid scaling.
export function referenceFontSize(value: string, rootSize: number, parentSize: number): number | null {
    const fixed = value.trim().match(/^([\d.]+)(px|rem|em)$/i)
        || value.trim().match(/^clamp\([\s\S]*,\s*([\d.]+)(px|rem|em)\s*\)$/i);
    if (!fixed) return null;
    const size = Number(fixed[1]) * ({ px: 1, rem: rootSize, em: parentSize }[fixed[2].toLowerCase()] || 1);
    return Number.isFinite(size) && size > 0 ? size : null;
}

export function observeDisplayHeadings(section: HTMLElement): () => void {
    const doc = section.ownerDocument;
    const view = doc.defaultView;
    const inner = section.querySelector<HTMLElement>(':scope > .stgb-presentation__inner');
    const content = inner?.querySelector<HTMLElement>(':scope > .stgb-presentation__content');
    if (!view || !inner || !content) return () => {};

    let frame = 0;
    let disposed = false;
    const headings = new Set<HTMLElement>();
    const widths = new WeakMap<Element, number>();
    const clear = (heading: HTMLElement) => {
        heading.removeAttribute(fitAttribute);
        heading.style.removeProperty(fitProperty);
    };
    const schedule = () => {
        if (!disposed && !frame) frame = view.requestAnimationFrame(update);
    };
    const resize = view.ResizeObserver ? new view.ResizeObserver((entries) => {
        // Font fitting changes height; only width changes need another pass.
        entries.forEach(({ target, contentRect }) => {
            if (widths.get(target) !== contentRect.width) {
                widths.set(target, contentRect.width);
                schedule();
            }
        });
    }) : null;
    const mutations = new view.MutationObserver(schedule);
    const observeContent = () => mutations.observe(content, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'style'],
    });

    function update() {
        frame = 0;
        // Do not observe our temporary sizing styles as authored changes.
        mutations.disconnect();
        try {
            const current = new Set(Array.from(content!.querySelectorAll<HTMLElement>(selector))
                .filter((heading) => heading.closest('.stgb-presentation') === section));
            headings.forEach((heading) => {
                if (current.has(heading)) return;
                clear(heading);
                resize?.unobserve(heading);
                headings.delete(heading);
            });
            const scale = Math.min(1, inner!.clientWidth / 1920);
            const rootSize = parseFloat(view!.getComputedStyle(doc.documentElement).fontSize);

            current.forEach((heading) => {
                if (!headings.has(heading)) {
                    headings.add(heading);
                    resize?.observe(heading);
                }
                clear(heading);
                if (!scale || !heading.clientWidth) return;
                const computed = view!.getComputedStyle(heading);
                const naturalSize = parseFloat(computed.fontSize);
                const parentSize = parseFloat(view!.getComputedStyle(heading.parentElement!).fontSize);
                let authored = heading.style.fontSize;
                // Presets can supply the same WordPress fluid clamp via a variable.
                if (!authored) {
                    const preset = Array.from(heading.classList).map((name) => name.match(/^has-(.+)-font-size$/)).find(Boolean);
                    if (preset) authored = computed.getPropertyValue(`--wp--preset--font-size--${preset[1]}`).trim();
                }
                authored = authored.replace(/var\((--[\w-]+)\)/g, (_, name) => computed.getPropertyValue(name).trim());
                const reference = referenceFontSize(authored, rootSize, parentSize);
                // Existing viewport/container units already scale; avoid scaling twice.
                let size = reference !== null ? reference * scale
                    : /(?:cqw|cqi|vw|vi|vmin|vmax)\b/i.test(authored) ? naturalSize : naturalSize * scale;
                if (!Number.isFinite(size) || size <= 0) return;
                const padding = (parseFloat(computed.paddingLeft) || 0) + (parseFloat(computed.paddingRight) || 0);
                const available = heading.clientWidth - padding;
                if (available <= 0) return;
                heading.style.setProperty(fitProperty, `${size}px`);
                heading.setAttribute(fitAttribute, '');
                // nowrap preserves intentional <br> breaks. Fit the widest line,
                // including when an inner Sidekick column constrains its width.
                for (let pass = 0; pass < 4 && heading.scrollWidth > heading.clientWidth; pass++) {
                    size *= Math.max(0.01, available - 1) / (heading.scrollWidth - padding);
                    heading.style.setProperty(fitProperty, `${size}px`);
                }
            });
        } finally {
            if (!disposed) observeContent();
        }
    }

    resize?.observe(inner);
    view.addEventListener('resize', schedule);
    doc.fonts?.ready.then(schedule);
    doc.fonts?.addEventListener('loadingdone', schedule);
    update();

    return () => {
        disposed = true;
        view.cancelAnimationFrame(frame);
        resize?.disconnect();
        mutations.disconnect();
        view.removeEventListener('resize', schedule);
        doc.fonts?.removeEventListener('loadingdone', schedule);
        headings.forEach(clear);
    };
}
